// API client for the web app
const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API ${status}: ${JSON.stringify(body)}`);
  }
}

async function request<T>(
  path: string,
  init?: Omit<RequestInit, 'body'> & { method?: string; body?: unknown },
): Promise<T> {
  const { method = 'GET', body, headers, ...rest } = init ?? {};

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
    ...rest,
  });

  if (!res.ok) {
    const errorBody = await res.text();
    throw new ApiError(res.status, errorBody);
  }
  return res.json() as Promise<T>;
}

// === Auth ===
export interface User {
  id: string;
  email: string;
  displayName: string;
  role: string;
  active: boolean;
  /** v1.28.3: 头像 URL (R2 public 路径), 缺时 fallback 色块+initials */
  avatarUrl?: string;
}

export interface CompanyInfo {
  id: string;
  code: 'JP' | 'HK' | 'CN';
  name: string;
  country: string;
  flag: string;
  isDefault: boolean;
  active: boolean;
}

export interface LoginResponse {
  user: User;
  session: { id: string; currentCompanyId: string; availableCompanyIds: string[] };
}

export interface MeResponse {
  user: User;
  currentCompanyId: string;
  availableCompanies: CompanyInfo[];
}

export const authApi = {
  register: (data: { email: string; password: string; displayName: string }) =>
    request<{ user: User }>('/auth/register', { method: 'POST', body: data }),
  login: (data: { email: string; password: string }) =>
    request<LoginResponse>('/auth/login', { method: 'POST', body: data }),
  logout: () => request<{ success: boolean }>('/auth/logout', { method: 'POST' }),
  me: () => request<MeResponse>('/auth/me'),
  updateProfile: (data: { displayName: string }) =>
    request<{ user: User }>('/auth/me', { method: 'PATCH', body: data }),
  updatePassword: (data: { currentPassword: string; newPassword: string }) =>
    request<{ success: boolean }>('/auth/me/password', { method: 'POST', body: data }),
  // v1.28.3: 头像上传 (multipart, 不走 request 通用函数, 自己 fetch)
  uploadAvatar: (
    file: File,
  ): Promise<{ avatarUrl: string; r2Key: string; size: number; type: string }> => {
    const fd = new FormData();
    fd.append('avatar', file);
    return fetch('/api/auth/me/avatar', {
      method: 'POST',
      body: fd,
      credentials: 'include',
    }).then((r) => {
      if (!r.ok) return r.json().then((e) => Promise.reject(new Error(e.message ?? '上传失败')));
      return r.json();
    });
  },
};

export const companyApi = {
  list: () => request<{ currentCompanyId: string; companies: CompanyInfo[] }>('/companies'),
  switch: (companyId: string) =>
    request<{ currentCompanyId: string; message: string }>('/companies/switch', {
      method: 'POST',
      body: { companyId },
    }),
  update: (
    id: string,
    patch: {
      name?: string;
      taxId?: string | null;
      defaultCurrency?: 'USD' | 'JPY' | 'EUR' | 'CNY' | 'HKD';
    },
  ) => request<{ company: CompanyDetail }>(`/companies/${id}`, { method: 'PATCH', body: patch }),
};

export interface CompanyDetail {
  id: string;
  code: 'JP' | 'HK' | 'CN';
  name: string;
  country: string;
  role: string;
  taxId?: string;
  defaultCurrency: string;
  active: boolean;
}

// === Customer ===
export interface Customer {
  id: string;
  companyId: string;
  name: string;
  country: string;
  city?: string;
  website?: string;
  source: string;
  sourceUrl?: string;
  stage: 'prospect' | 'contacted' | 'quoted' | 'sampled' | 'ordered' | 'delivered' | 'lost';
  incotermPref?: string;
  paymentPref?: string;
  currencyPref?: string;
  tags: string[];
  notes?: string;
  // v1.9.1: 背调字段 (ANTI-FRAUD Part 1 SOP)
  backgroundCheckStatus?: 'pending' | 'in_progress' | 'passed' | 'failed' | 'waived' | null;
  backgroundCheckDate?: number;
  backgroundCheckSource?: 'manual' | 'sinosure' | 'historical' | null;
  redFlags?: RedFlag[];
  firstOrderPayment?: string;
  createdAt: number;
  updatedAt: number;
  lastInteraction?: number;
}

export interface RedFlag {
  flag: string;
  severity: 'low' | 'medium' | 'high';
  autoDetected: boolean;
  detectedAt?: number;
}

export type CustomerGrade = 'A' | 'B' | 'C';

export interface BackgroundCheckResult {
  status: Customer['backgroundCheckStatus'];
  date?: number;
  source?: Customer['backgroundCheckSource'];
  redFlags: RedFlag[];
  firstOrderPayment?: string;
  grade: CustomerGrade;
  recommendedPayment: string;
}

export const customerApi = {
  list: (
    filters: {
      stage?: string;
      country?: string;
      search?: string;
      limit?: number;
      offset?: number;
      sort?: 'createdAt' | 'updatedAt' | 'name' | 'recentActivity';
      order?: 'asc' | 'desc';
    } = {},
  ) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.set(k, String(v));
    });
    const q = params.toString();
    return request<{ items: Customer[]; total: number }>(`/customers${q ? `?${q}` : ''}`);
  },
  get: (id: string) => request<Customer>(`/customers/${id}`),
  create: (data: Omit<Customer, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>) =>
    request<Customer>('/customers', { method: 'POST', body: data }),
  update: (id: string, patch: Partial<Customer>) =>
    request<Customer>(`/customers/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => request<{ success: boolean }>(`/customers/${id}`, { method: 'DELETE' }),
  // v1.9.1: 背调 (rule-based, 4 步 SOP) — 跟 v1.9.2 /scan 互补
  backgroundCheck: (id: string) =>
    request<BackgroundCheckResult>(`/customers/${id}/background-check`),
  analyzeBackgroundCheck: (
    id: string,
    data: { email?: string; contactPerson?: string; contactPhone?: string },
  ) =>
    request<BackgroundCheckResult>(`/customers/${id}/background-check`, {
      method: 'POST',
      body: data,
    }),
  saveBackgroundCheck: (
    id: string,
    data: {
      status?: Customer['backgroundCheckStatus'];
      source?: string;
      firstOrderPayment?: string;
    },
  ) =>
    request<{
      status: Customer['backgroundCheckStatus'];
      date?: number;
      source?: Customer['backgroundCheckSource'];
      firstOrderPayment?: string;
    }>(`/customers/${id}/background-check`, { method: 'PATCH', body: data }),
  setRedFlags: (id: string, action: 'add' | 'remove', flag: RedFlag) =>
    request<{ redFlags: RedFlag[] }>(`/customers/${id}/red-flags`, {
      method: 'POST',
      body: { action, flag },
    }),
  // v1.9.2: 一键背调扫描
  scan: (id: string, overrides?: { email?: string; industry?: string; contactPerson?: string }) =>
    request<BackgroundCheckScan>(`/customers/${id}/scan`, {
      method: 'POST',
      body: overrides ?? {},
    }),
  scanHistory: (id: string) =>
    request<{ items: BackgroundCheckScan[]; total: number }>(`/customers/${id}/scan/history`),
  // Dashboard widget: 找出"今日待扫描"客户
  neverScanned: () =>
    request<{
      items: Array<{ id: string; name: string; country: string }>;
      total: number;
    }>('/customers/scan/never-scanned'),
};

// === v1.9.2: Background Check ===
export interface BackgroundCheckScan {
  id: string;
  customerId: string;
  scannedAt: number;
  score: number;
  level: 'A' | 'B' | 'C';
  signals: Array<{ name: string; severity: 'high' | 'medium' | 'low'; evidence: string }>;
  suggestedAction: string;
  aiModel: string;
}

// === Inquiry ===
export interface Inquiry {
  id: string;
  companyId: string;
  customerId: string;
  source: string;
  sourceRef?: string;
  subject?: string;
  body?: string;
  language: 'zh' | 'en' | 'ja';
  receivedAt: number;
  productHint?: {
    category?: string;
    qty?: number;
    craft?: string[];
    /** v1.36+: AI 提取的紧急度 */
    urgency?: string;
    /** v1.36+: 预算 (数字或区间字符串) */
    budget?: number | string;
    /** v1.36+: 偏好联系方式 (email/wechat/phone) */
    preferredContact?: string;
    /** AI 提取的原文片段 */
    rawText?: string;
  };
  status: 'new' | 'quoted' | 'sampled' | 'ordered' | 'rejected' | 'archived';
  /** v1.36+: 分配的跟进人 (预留字段, schema 已存但 UI 之前没显示) */
  assignedTo?: string | null;
  createdAt: number;
  updatedAt: number;
}

export const inquiryApi = {
  list: (filters: { status?: string; customerId?: string } = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, String(v)));
    const q = params.toString();
    return request<{ items: Inquiry[]; total: number }>(`/inquiries${q ? `?${q}` : ''}`);
  },
  get: (id: string) =>
    request<
      Inquiry & {
        customer?: {
          id: string;
          name: string;
          email: string | null;
          country: string;
          website?: string | null;
          tags?: string[] | null;
        };
      }
    >(`/inquiries/${id}`),
  create: (data: Partial<Inquiry> & { customerId: string; source: string }) =>
    request<Inquiry>('/inquiries', { method: 'POST', body: data }),
  update: (id: string, patch: Partial<Inquiry>) =>
    request<Inquiry>(`/inquiries/${id}`, { method: 'PATCH', body: patch }),
  // v1.22: 起草回复 (AI 生成, 失败走模板)
  replyDraft: (
    id: string,
    data: { language?: 'ja' | 'en' | 'zh'; tone?: 'professional' | 'friendly' } = {},
  ) =>
    request<{
      subject: string;
      body: string;
      language: 'ja' | 'en' | 'zh';
      generatedBy: 'ai' | 'rule';
    }>(`/inquiries/${id}/reply-draft`, { method: 'POST', body: data }),
  // v1.34+: 发送回复邮件 (Resend)
  // v1.37.7: 后端不再"假成功" — 发给客户失败时返 502 + ok:false,
  //   询盘状态不推进、不写 reply_message。前端要能把 backupSentToOwner
  //   这个信息透出来, 否则用户以为"发成功了"。
  sendReply: (id: string, data: { to?: string; subject?: string; body?: string } = {}) =>
    request<{
      ok: boolean;
      resendId?: string;
      to?: string;
      subject?: string;
      error?: string;
      dryRun?: boolean;
      message?: string;
      /** v1.36+: Resend 测试模式 fallback notice */
      fallback?: string;
      /** v1.37.7: 是否真投递到客户邮箱 (false = 客户啥也没收到) */
      deliveredToCustomer?: boolean;
      /** v1.37.7: 客户没收到时, 内容是否已备份发到 owner */
      backupSentToOwner?: boolean;
    }>(`/inquiries/${id}/send-reply`, { method: 'POST', body: data }),
  // v1.22: 标记已回复
  markReplied: (
    id: string,
    data: { method: 'email' | 'phone' | 'wechat'; note?: string } = { method: 'email' },
  ) =>
    request<{ success: boolean; inquiry: Inquiry }>(`/inquiries/${id}/mark-replied`, {
      method: 'POST',
      body: data,
    }),
  // v1.36+: 翻译 (Qwen3-30B AI 翻译, 支持 ja / en / zh)
  translate: (
    id: string,
    data: { target: 'ja' | 'en' | 'zh'; field?: 'body' | 'subject' } = { target: 'zh' },
  ) =>
    request<{
      ok: boolean;
      inquiryId: string;
      field: string;
      source: string;
      target: string;
      translated: string;
      generatedBy: string;
      generatedAt: number;
      error?: string;
    }>(`/inquiries/${id}/translate`, { method: 'POST', body: data }),
  // v1.36+: 翻译任意 text (用于 "再翻译" — 改完 draft 后翻回 draftLang)
  translateText: (id: string, data: { target: 'ja' | 'en' | 'zh'; text: string }) =>
    request<{
      ok: boolean;
      target: string;
      translated: string;
      generatedBy: string;
      generatedAt: number;
      error?: string;
    }>(`/inquiries/${id}/translate-text`, { method: 'POST', body: data }),
  // v1.27: 一键加客户
  convertToCustomer: (
    id: string,
    data: { name?: string; email?: string; country?: string; website?: string } = {},
  ) =>
    request<{ success: boolean; customer: { id: string; name: string } }>(
      `/inquiries/${id}/convert-to-customer`,
      { method: 'POST', body: data },
    ),
  // v1.27: 飞书草稿列表
  feishuDrafts: (id: string) =>
    request<{
      items: {
        id: string;
        inquiryId: string;
        source: string;
        userOpenId: string;
        language: string;
        tone: string;
        subject?: string;
        body: string;
        generatedBy: string;
        createdAt: number;
      }[];
    }>(`/inquiries/${id}/feishu-drafts`),
  // v1.27: 回复消息列表
  replyMessages: (id: string) =>
    request<{
      items: {
        id: string;
        inquiryId: string;
        source: string;
        direction: 'inbound' | 'outbound';
        subject?: string;
        body: string;
        createdAt: number;
      }[];
    }>(`/inquiries/${id}/reply-messages`),
  // v1.38: 统一收件箱 — 线程内消息列表 (inquiry 即线程)
  messages: (id: string) => request<{ items: InboxMessage[] }>(`/inquiries/${id}/messages`),
  // v1.38: 标记单条消息已读 (幂等)
  markMessageRead: (id: string, messageId: string) =>
    request<{ ok: boolean }>(`/inquiries/${id}/messages/${messageId}/read`, { method: 'POST' }),
  // v1.38: 出站草稿 — 中文意图 → 目标语言专业回复 (AI 不可用时降级为 rule, 不 500)
  draft: (
    id: string,
    data: { text: string; target: 'ja' | 'en' | 'zh'; tone?: 'professional' | 'friendly' },
  ) =>
    request<{
      ok: boolean;
      target: string;
      subject: string;
      body: string;
      generatedBy: 'ai' | 'rule';
    }>(`/inquiries/${id}/draft`, { method: 'POST', body: data }),
};

// === Quote ===
export interface Quote {
  id: string;
  companyId: string;
  customerId: string;
  inquiryId?: string;
  quoteNumber: string;
  incoterm: 'EXW' | 'FOB' | 'CFR' | 'CIF' | 'DAP' | 'DDP';
  incotermPlace: string;
  currency: 'USD' | 'JPY' | 'EUR' | 'CNY' | 'HKD';
  exwTotalCents: number;
  fobTotalCents?: number;
  cfrTotalCents?: number;
  cifTotalCents?: number;
  dapTotalCents?: number;
  ddpTotalCents?: number;
  selectedTotalCents: number;
  paymentTerms: string;
  validUntil: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  sentAt?: number;
  acceptedAt?: number;
  rejectedAt?: number;
  notes?: string;
  quoteParams?: QuoteParams;
  createdAt: number;
  updatedAt: number;
}

export interface QuoteParams {
  exwUnitPriceCents: number;
  qty: number;
  innerPackCostPerUnitCents: number;
  totalCbm: number;
  cartons: number;
  freightRateUsdCentsPerCbm: number;
  totalGrossKg: number;
  insuranceRate: number;
  destCustomsDutyRate: number;
  destVatRate: number;
  destLastMileUsdCents: number;
  originPort: string;
  destPort: string;
  destCountry: string;
}

export interface QuoteItem {
  id: string;
  quoteId: string;
  name: string;
  spec?: string;
  qty: number;
  unit: string;
  unitPriceCents: number;
  totalCents: number;
  hsCode?: string;
}

export const quoteApi = {
  list: (filters: { status?: string; customerId?: string } = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, String(v)));
    const q = params.toString();
    return request<{ items: Quote[]; total: number }>(`/quotes${q ? `?${q}` : ''}`);
  },
  get: (id: string) => request<Quote & { items: QuoteItem[] }>(`/quotes/${id}`),
  create: (data: {
    customerId: string;
    inquiryId?: string;
    exwUnitPriceCents: number;
    qty: number;
    innerPackCostPerUnitCents?: number;
    totalCbm: number;
    cartons?: number;
    originPort?: string;
    destPort: string;
    destCountry: string;
    targetIncoterm: Quote['incoterm'];
    targetCurrency: Quote['currency'];
    freightRateUsdCentsPerCbm?: number;
    insuranceRate?: number;
    destCustomsDutyRate?: number;
    destVatRate?: number;
    destLastMileUsdCents?: number;
    paymentTerms?: string;
    validDays?: number;
    notes?: string;
  }) => request<Quote & { items: QuoteItem[] }>('/quotes', { method: 'POST', body: data }),
  update: (id: string, patch: Partial<Quote>) =>
    request<Quote>(`/quotes/${id}`, { method: 'PATCH', body: patch }),
};

// === Coach ===
export type CoachMode = 'free' | 'knowledge' | 'quiz' | 'scenario';

export interface CoachSession {
  id: string;
  userId: string;
  companyId: string;
  mode: CoachMode;
  refId: string | null;
  title: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface CoachMessage {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  meta: { knowledgeIds?: string[] } | null;
  createdAt: number;
}

export interface CoachKnowledge {
  id: string;
  category: string;
  title: string;
  content: string;
  tags: string[] | null;
  source: string;
  sourceUrl: string | null;
  lastVerified: number;
}

export const coachApi = {
  chat: (data: { message: string; sessionId?: string; mode?: CoachMode; refId?: string }) =>
    request<{
      sessionId: string;
      mode: CoachMode;
      reply: string;
      sources?: string[];
      messageId: string;
    }>('/coach/chat', { method: 'POST', body: data }),
  /**
   * v1.7: SSE 流式版 coach chat
   * - 通过 fetch + ReadableStream reader 解析 server-sent events
   * - 事件类型: start / delta / done / error
   * - 客户端可通过 AbortSignal 取消
   * - Promise 在流读完/出错/被 abort 时 resolve (abort 不抛错)
   */
  chatStream: async (data: {
    message: string;
    sessionId?: string;
    mode?: CoachMode;
    refId?: string;
    signal?: AbortSignal;
    onStart: (info: {
      sessionId: string;
      userMessageId: string;
      messageId: string;
      sources?: string[];
      mode: CoachMode;
    }) => void;
    onDelta: (delta: string) => void;
    onDone: (info: { messageId: string; content: string; sources?: string[] }) => void;
    onError: (err: { message: string; fallback?: string }) => void;
  }): Promise<void> => {
    const res = await fetch(`${API_BASE}/coach/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        message: data.message,
        sessionId: data.sessionId,
        mode: data.mode,
        refId: data.refId,
      }),
      signal: data.signal,
    });

    if (!res.ok || !res.body) {
      const bodyText = await res.text().catch(() => '');
      throw new ApiError(res.status, bodyText);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let aborted = false;

    if (data.signal) {
      if (data.signal.aborted) {
        aborted = true;
        try {
          await reader.cancel();
        } catch {
          // ignore
        }
        return;
      }
      data.signal.addEventListener('abort', () => {
        aborted = true;
        reader.cancel().catch(() => {
          // ignore
        });
      });
    }

    while (true) {
      if (aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE: 事件以 \n\n 分隔
      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';
      for (const event of events) {
        if (aborted) break;
        let eventType = 'message';
        let payload = '';
        for (const rawLine of event.split('\n')) {
          const line = rawLine.replace(/\r$/, '');
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            payload += line.slice(5).trim();
          }
        }
        if (!payload) continue;
        let parsed: unknown;
        try {
          parsed = JSON.parse(payload);
        } catch {
          continue;
        }
        const obj = parsed as Record<string, unknown>;
        if (eventType === 'start') {
          data.onStart({
            sessionId: String(obj.sessionId ?? ''),
            userMessageId: String(obj.userMessageId ?? ''),
            messageId: String(obj.messageId ?? ''),
            sources: Array.isArray(obj.sources) ? (obj.sources as string[]) : undefined,
            mode: (obj.mode as CoachMode) ?? 'free',
          });
        } else if (eventType === 'delta') {
          const content = typeof obj.content === 'string' ? obj.content : '';
          if (content.length > 0) data.onDelta(content);
        } else if (eventType === 'done') {
          data.onDone({
            messageId: String(obj.messageId ?? ''),
            content: typeof obj.content === 'string' ? obj.content : '',
            sources: Array.isArray(obj.sources) ? (obj.sources as string[]) : undefined,
          });
        } else if (eventType === 'error') {
          data.onError({
            message: typeof obj.message === 'string' ? obj.message : 'stream_error',
            fallback: typeof obj.fallback === 'string' ? obj.fallback : undefined,
          });
        }
      }
    }
  },
  listSessions: () => request<{ sessions: CoachSession[] }>('/coach/sessions'),
  getSession: (id: string) =>
    request<{ session: CoachSession; messages: CoachMessage[] }>(`/coach/sessions/${id}`),
  listKnowledge: (category?: string) =>
    request<{ knowledge: CoachKnowledge[] }>(
      `/coach/knowledge${category ? `?category=${category}` : ''}`,
    ),
  quizStart: (data: { mode: 'quiz'; questionCount?: number }) =>
    request<{
      sessionId: string;
      questions: Array<{
        id: string;
        category: string;
        type: 'mcq' | 'calculation' | 'judgment' | 'scenario';
        difficulty: 'easy' | 'medium' | 'hard';
        question: string;
        options?: Array<{ id: string; text: string }>;
        explanation: string;
        referenceKnowledgeIds: string[];
      }>;
      total: number;
    }>('/coach/quiz/start', { method: 'POST', body: data }),
  quizAnswer: (data: { sessionId: string; questionId: string; userAnswer: string }) =>
    request<{
      correct: boolean;
      rating: 'correct' | 'partial' | 'wrong';
      explanation: string;
      matchedCriteria?: string[];
      missingCriteria?: string[];
      expectedAnswer: string;
      score: number;
      total: number;
      nextQuestion: {
        id: string;
        category: string;
        type: 'mcq' | 'calculation' | 'judgment' | 'scenario';
        difficulty: 'easy' | 'medium' | 'hard';
        question: string;
        options?: Array<{ id: string; text: string }>;
        explanation: string;
        referenceKnowledgeIds: string[];
      } | null;
    }>('/coach/quiz/answer', { method: 'POST', body: data }),
  // ============ v1.8 P1: Training mode (对话式模拟) ============
  listTrainingScenarios: () =>
    request<{
      scenarios: Array<{
        id: string;
        name: string;
        description: string;
        difficulty: 1 | 2 | 3;
        customerPersona: string;
        customerMessage: string;
        category: string;
      }>;
    }>('/coach/training/scenarios'),
  trainingStart: (data: { scenarioId: string }) =>
    request<{
      sessionId: string;
      scenario: {
        id: string;
        name: string;
        description: string;
        difficulty: 1 | 2 | 3;
        customerPersona: string;
      };
      customerMessage: string;
      turn: number;
      maxTurns: number;
    }>('/coach/training/start', { method: 'POST', body: data }),
  trainingRespond: (data: { sessionId: string; userMessage: string }) =>
    request<{
      sessionId: string;
      turn: number;
      maxTurns: number;
      done: boolean;
      score: number;
      feedback: string;
      redlineFlags: string[];
      matchedBehaviors: string[];
      missingBehaviors: string[];
      customerMessage: string;
      totalScore?: number;
    }>('/coach/training/respond', { method: 'POST', body: data }),
  getTrainingSession: (id: string) =>
    request<{
      session: {
        id: string;
        scenarioId: string;
        status: 'active' | 'completed' | 'abandoned';
        score: number | null;
        startedAt: number;
        endedAt: number | null;
      };
      messages: Array<{
        id: string;
        role: 'customer' | 'user';
        content: string;
        score: number | null;
        redlineFlags: string[] | null;
        createdAt: number;
      }>;
    }>(`/coach/training/session/${id}`),
  // ============ v1.10: 反骗术训练 (fraud mode) ============
  fraudListScenarios: () =>
    request<{
      scenarios: Array<{
        id: string;
        name: string;
        description: string;
        difficulty: 1 | 2 | 3;
        customerPersona: string;
        customerMessage: string;
        fraudCategory: string;
        referenceDoc: string;
      }>;
    }>('/coach/fraud/scenarios'),
  fraudGetScenario: (id: string) =>
    request<{
      scenario: {
        id: string;
        name: string;
        description: string;
        difficulty: 1 | 2 | 3;
        customerPersona: string;
        customerMessage: string;
        fraudCategory: string;
        referenceDoc: string;
        expectedBehaviors: string[];
        redLines: string[];
      };
    }>(`/coach/fraud/scenarios/${id}`),
  fraudStart: (data: { scenarioId: string }) =>
    request<{
      sessionId: string;
      scenario: {
        id: string;
        name: string;
        description: string;
        difficulty: 1 | 2 | 3;
        customerPersona: string;
        fraudCategory: string;
        referenceDoc: string;
      };
      customerMessage: string;
      turn: number;
      maxTurns: number;
    }>('/coach/fraud/start', { method: 'POST', body: data }),
  fraudRespond: (data: { sessionId: string; userMessage: string }) =>
    request<{
      sessionId: string;
      turn: number;
      maxTurns: number;
      done: boolean;
      score: number;
      feedback: string;
      grade?: 'A' | 'B' | 'C';
      gradeLabel?: string;
      redlineFlags: string[];
      matchedBehaviors: string[];
      missingBehaviors: string[];
      customerMessage: string;
      totalScore?: number;
    }>('/coach/fraud/respond', { method: 'POST', body: data }),
};

// === Compliance ===
export type ComplianceRiskLevel = 'low' | 'medium' | 'high';

export interface ComplianceCheckItem {
  category: string;
  item: string;
  required: boolean;
  deadline?: string;
  note?: string;
}

export interface ComplianceCheckResult {
  checklist: ComplianceCheckItem[];
  riskLevel: ComplianceRiskLevel;
}

export const complianceApi = {
  check: (data: {
    destination: string;
    product: string;
    targetMarket: string;
    productCategory?: string;
  }) => request<ComplianceCheckResult>('/compliance/check', { method: 'POST', body: data }),
};

// === Health ===
export interface HealthInfo {
  status: string;
  timestamp?: string;
  environment?: string;
  version?: string;
  services?: { d1: boolean; r2: boolean; ai: boolean };
}

export const healthApi = {
  check: () => request<HealthInfo>('/health'),
};

// v1.15: 系统状态 (Dashboard 用) — D1 / R2 / AI 实时探测
export type ServiceStatus = 'ok' | 'degraded' | 'down';

export interface ServiceCheck {
  status: ServiceStatus;
  latencyMs: number;
  message?: string;
}

export interface SystemStatusInfo {
  api: ServiceCheck;
  services: {
    database: ServiceCheck;
    storage: ServiceCheck;
    ai: ServiceCheck;
  };
  build: {
    version: string;
    environment: string;
    commit: string;
  };
  checkedAt: string;
}

export const systemApi = {
  status: () => request<SystemStatusInfo>('/system/status'),
};

// === Order ===
export type OrderStatus =
  | 'draft'
  | 'deposit_received'
  | 'producing'
  | 'qc'
  | 'balance_received'
  | 'shipped'
  | 'closed'
  | 'cancelled';

export interface Order {
  id: string;
  companyId: string;
  customerId: string;
  poNumber?: string;
  status: OrderStatus;
  incoterm: string;
  incotermPlace: string;
  totalAmountCents: number;
  currency: string;
  depositPct: number;
  shipDate?: number;
  etaDate?: number;
  postmortem?: string;
  postmortemError?: string;
  notes?: string;
  createdAt: number;
  updatedAt: number;
  checklistProgress: {
    incoterm: string;
    totalSteps: number;
    completedSteps: number;
    steps: Array<{
      stepId: string;
      status: 'pending' | 'in_progress' | 'done' | 'skipped';
      completedAt?: number;
      note?: string;
    }>;
  };
  checklist: Array<{
    id: string;
    index: number;
    title: { zh: string; en: string; ja: string };
    description: { zh: string; en: string; ja: string };
    why?: { zh: string; en: string; ja: string }; // v1.8 P0: 为什么这步重要
    requiredDocs: string[];
    blocksShipping?: boolean;
    coachHint: { zh: string; en: string; ja: string };
  }>;
}

export interface CreateOrderInput {
  customerId: string;
  quoteId?: string;
  poNumber?: string;
  type?: 'sample' | 'bulk';
  incoterm: 'EXW' | 'FOB' | 'CFR' | 'CIF' | 'DAP' | 'DDP';
  incotermPlace: string;
  totalAmountCents: number;
  currency?: string;
  depositPct?: number;
  shipDate?: number;
  etaDate?: number;
  notes?: string;
}

export const orderApi = {
  list: (filters: { status?: OrderStatus; customerId?: string } = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, String(v)));
    const q = params.toString();
    return request<{ items: Order[]; total: number }>(`/orders${q ? `?${q}` : ''}`);
  },
  get: (id: string) => request<Order>(`/orders/${id}`),
  create: (data: CreateOrderInput) => request<Order>('/orders', { method: 'POST', body: data }),
  update: (
    id: string,
    patch: { status?: OrderStatus; shipDate?: number; etaDate?: number; notes?: string },
  ) => request<Order>(`/orders/${id}`, { method: 'PATCH', body: patch }),
  completeStep: (id: string, stepId: string, note?: string) =>
    request<Order>(`/orders/${id}/steps/${stepId}/complete`, {
      method: 'POST',
      body: { stepId, note },
    }),
};

// === Sourcing ===
export interface DorkTemplate {
  id: string;
  category: string;
  market: string;
  title: { zh: string; en: string; ja: string };
  query: string;
  description: { zh: string; en: string; ja: string };
}

export interface DirectoryEntry {
  id: string;
  market: string;
  name: string;
  url: string;
  description: { zh: string; en: string; ja: string };
  searchHint: string;
}

export interface SourcingGenerateResult {
  dorks: Array<{ query: string; intent: string; tags: string[] }>;
  directories: Array<{ name: string; url: string; note: string }>;
}

export const sourcingApi = {
  listDorks: (market?: string) =>
    request<{ dorks: DorkTemplate[] }>(`/sourcing/dorks${market ? `?market=${market}` : ''}`),
  listDirectories: (market?: string) =>
    request<{ directories: DirectoryEntry[] }>(
      `/sourcing/directories${market ? `?market=${market}` : ''}`,
    ),
  generate: (data: {
    product: string;
    city?: string;
    country?: string;
    intent: 'wholesale' | 'manufacturer' | 'retailer' | 'distributor';
  }) => request<SourcingGenerateResult>('/sourcing/generate', { method: 'POST', body: data }),
  // v1.6 Dork Hybrid
  listTemplates: () =>
    request<{
      templates: Array<{
        id: string;
        template: string;
        industry: string;
        language: string;
        descriptionZh: string;
        example: string;
        scenarioTags: string[];
        enabled: boolean;
      }>;
      modifiers: Array<{
        id: string;
        modifier: string;
        reason: string;
        category: string;
      }>;
      total: number;
    }>('/sourcing/templates'),
  fill: (data: {
    product: string;
    country?: string;
    city?: string;
    intent?: 'wholesale' | 'manufacturer' | 'retailer' | 'distributor';
    industry?: 'toy' | 'figurine' | 'collectible' | 'plush' | 'craft' | 'universal';
    count?: number;
  }) =>
    request<{
      dorks: Array<{
        query: string;
        templateId: string;
        description: string;
        reason: string;
      }>;
      directories: Array<{ name: string; url: string; note: string }>;
      fallback: boolean;
    }>('/sourcing/fill', { method: 'POST', body: data }),
  derive: (data: { baseQuery: string; modifiers?: string[]; count?: number }) =>
    request<{
      variants: Array<{
        query: string;
        modifiers: string[];
        reason: string;
      }>;
      fallback: boolean;
    }>('/sourcing/derive', { method: 'POST', body: data }),
  listFeedback: (limit = 20) =>
    request<{
      feedback: Array<{
        id: string;
        dorkQuery: string;
        dorkTemplateId: string | null;
        rating: 'useful' | 'useless';
        searchEngine: string;
        createdAt: number;
      }>;
      bias: { preferredTemplateIds: string[]; avoidedPatterns: string[] };
      total: number;
    }>(`/sourcing/feedback?limit=${limit}`),
  postFeedback: (data: {
    dorkQuery: string;
    dorkTemplateId?: string;
    rating: 'useful' | 'useless';
  }) => request<{ ok: boolean }>('/sourcing/feedback', { method: 'POST', body: data }),
  // v1.7 P1 Layer 5: 实际抓 Google/Bing/DDG
  search: (params: { dork: string; engine?: 'google' | 'bing' | 'duckduckgo'; limit?: number }) => {
    const qs = new URLSearchParams({ dork: params.dork });
    if (params.engine) qs.set('engine', params.engine);
    if (params.limit) qs.set('limit', String(params.limit));
    return request<{
      engine: 'google' | 'bing' | 'duckduckgo';
      dork: string;
      results: Array<{ title: string; url: string; snippet: string }>;
      fetchStatus:
        | 'ok'
        | 'captcha'
        | 'rate_limited'
        | 'network_error'
        | 'parse_error'
        | 'no_results';
      fetchedAt: number;
      cached: boolean;
      errorMessage?: string;
    }>(`/sourcing/search?${qs.toString()}`);
  },
};

// === Email Templates (v1.8 P1) ===
export type EmailLang = 'zh' | 'en' | 'ja';
export type EmailCategory = 'outreach' | 'quote' | 'retention';

export interface EmailTemplateVar {
  name: string;
  label: string;
  hint?: string;
}

export interface EmailTemplate {
  id: string;
  key: string;
  name: string;
  category: EmailCategory;
  description: string;
  variables: EmailTemplateVar[];
  langs: Array<{
    id: string;
    key: string;
    name: string;
    category: EmailCategory;
    description: string;
    variables: EmailTemplateVar[];
    lang: EmailLang;
    subject: string;
    body: string;
  }>;
}

export const emailApi = {
  listTemplates: () =>
    request<{
      templates: EmailTemplate[];
      total: number;
      expectedTotal: number;
    }>('/email-templates'),
  getTemplate: (key: string, lang: EmailLang = 'zh') =>
    request<{
      template: {
        id: string;
        key: string;
        name: string;
        category: EmailCategory;
        description: string;
        variables: EmailTemplateVar[];
        lang: EmailLang;
        subject: string;
        body: string;
      };
    }>(`/email-templates/${key}?lang=${lang}`),
  renderTemplate: (key: string, lang: EmailLang, vars: Record<string, string>) => {
    const qs = new URLSearchParams({ lang });
    for (const [k, v] of Object.entries(vars)) {
      if (v) qs.set(k, v);
    }
    return request<{
      subject: string;
      body: string;
      variables: EmailTemplateVar[];
      providedVars: string[];
      missingVars: string[];
    }>(`/email-templates/${key}/render?${qs.toString()}`);
  },
  seedStatus: () =>
    request<{
      seeded: boolean;
      currentCount?: number;
      expectedTotal?: number;
      reason?: string;
      error?: string;
    }>('/email-templates/seed-status'),
};

// === Collection / Sinosure (v1.11) ===
export type CollectionLang = 'zh' | 'en';
export type CollectionStage = 'D+1' | 'D+7' | 'D+14' | 'D+30';
export type CollectionTemplateKey = 'D1_REMINDER' | 'D7_WARNING' | 'D14_URGENT' | 'D30_FINAL';

export interface CollectionTemplateVar {
  name: string;
  label: string;
  hint?: string;
}

export interface CollectionTemplate {
  id: string;
  key: CollectionTemplateKey;
  name: string;
  stage: CollectionStage;
  daysOverdue: number;
  description: string;
  variables: CollectionTemplateVar[];
  lang: CollectionLang;
  subject: string;
  body: string;
}

export type SinosureIncidentType =
  | 'late_payment'
  | 'account_hijack'
  | 'cargo_released'
  | 'fake_buyer';

export interface SinosureTimelineEvent {
  date: string;
  type: string;
  description: string;
}

export interface SinosureEvidence {
  name: string;
  type: 'contract' | 'pi' | 'bank_slip' | 'email' | 'b_l' | 'chat_log' | 'other';
  location?: string;
  note?: string;
}

export interface OverdueOrder {
  orderId: string;
  poNumber: string | null;
  customerId: string;
  incoterm: string;
  incotermPlace: string;
  totalAmountCents: number;
  currency: string;
  dueDate: number;
  daysOverdue: number;
  recommendedStage: CollectionTemplateKey;
  status: string;
}

export const collectionApi = {
  listTemplates: () =>
    request<{
      templates: CollectionTemplate[];
      total: number;
      expectedTotal: number;
    }>('/collection/templates'),

  getTemplate: (key: string, lang: CollectionLang = 'zh') =>
    request<{ template: CollectionTemplate }>(`/collection/templates/${key}?lang=${lang}`),

  generate: (data: {
    orderId: string;
    key: CollectionTemplateKey;
    variables?: Record<string, string>;
    lang?: CollectionLang;
  }) =>
    request<{
      subject: string;
      body: string;
      template: { key: string; name: string; stage: CollectionStage; daysOverdue: number };
      variables: CollectionTemplateVar[];
      providedVars: string[];
      missingVars: string[];
      autoVars: Record<string, string>;
    }>('/collection/generate', { method: 'POST', body: data }),

  sinosureReport: (data: {
    orderId: string;
    customerId: string;
    incidentType: SinosureIncidentType;
    amountUsd: number;
    timeline?: SinosureTimelineEvent[];
    evidence?: SinosureEvidence[];
    caseNote?: string;
    reportingEntity?: string;
  }) =>
    request<{
      markdown: string;
      filename: string;
      incidentType: SinosureIncidentType;
      incidentLabel: string;
      estimatedLossUsd: number;
    }>('/collection/sinosure-report', { method: 'POST', body: data }),
};

export const overdueApi = {
  list: () => request<{ items: OverdueOrder[]; total: number }>('/orders/overdue'),
};

// === Finance (v1.18+v1.31 UI) ===
// 应收 / 利润 / 退税 / 现金流 4 模块
// 完整 endpoint 见 apps/api/src/routes/finance.ts

export type AgingBucket = '0-30' | '31-60' | '61-90' | '90+';

export interface OrderAR {
  orderId: string;
  poNumber: string | null;
  customerId: string;
  totalAmountCents: number;
  paidCents: number;
  outstandingCents: number;
  currency: string;
  etaDate: number | null;
  dueDate: number | null;
  daysOverdue: number;
  agingBucket: AgingBucket | 'current' | 'no_due_date';
  status: string;
  statusLabel: string;
}

export interface ARSummary {
  totalOutstandingCents: number;
  totalOrders: number;
  currency: string;
  buckets: Record<AgingBucket | 'current', { count: number; outstandingCents: number }>;
  asOf: number;
}

export interface ProfitBreakdown {
  portChargesCents: number;
  oceanFreightCents: number;
  insuranceCents: number;
  totalCostCents: number;
}

export interface ProfitResult {
  orderId: string;
  revenueCents: number;
  costCents: number;
  breakdown: ProfitBreakdown;
  profitCents: number;
  profitMargin: number;
  currency: string;
  ratesUsed: {
    portRate: number;
    oceanRate: number;
    insuranceRate: number;
  };
  costSource: 'order.productCostCents' | 'estimated';
  asOf: number;
}

export interface ProfitSummary {
  month: string;
  orderCount: number;
  revenueCents: number;
  costCents: number;
  profitCents: number;
  avgMargin: number;
  currency: string;
}

export interface TaxRefundResult {
  orderId: string;
  productCostCents: number | null;
  taxBaseCents: number;
  refundRate: number;
  taxRefundCents: number;
  currency: string;
  eligible: boolean;
  reason: string | null;
  asOf: number;
}

export interface TaxRefundSummary {
  month: string;
  orderCount: number;
  totalRefundCents: number;
  currency: string;
  refundRate: number;
}

export interface DailyCashflowItem {
  date: string;
  inflowCents: number;
  outflowCents: number;
  netCents: number;
  orderCount: number;
}

export interface CashflowForecast {
  days: number;
  startDate: string;
  endDate: string;
  expectedInflowCents: number;
  expectedOutflowCents: number;
  netCashflowCents: number;
  currency: string;
  dailyBreakdown: DailyCashflowItem[];
  asOf: number;
  notes: string[];
}

export const financeApi = {
  // 应收 (Accounts Receivable)
  listAR: () => request<{ items: OrderAR[]; summary: ARSummary; total: number }>('/finance/ar'),
  arSummary: () => request<{ summary: ARSummary }>('/finance/ar/summary'),
  arByOrder: (orderId: string) =>
    request<{ order: { id: string }; ar: OrderAR }>(`/finance/ar/${orderId}`),

  // 利润
  profitByOrder: (
    orderId: string,
    rates?: { portRate?: number; oceanRate?: number; insuranceRate?: number },
  ) =>
    request<{
      order: { id: string; totalAmountCents: number; currency: string };
      profit: ProfitResult;
    }>(`/finance/profit/${orderId}`, { method: 'POST', body: rates ?? {} }),
  profitSummary: (granularity: 'month' | 'quarter' = 'month') =>
    request<{ items: ProfitSummary[]; granularity: string; total: number }>(
      `/finance/profit/summary?granularity=${granularity}`,
    ),

  // 退税
  taxRefundByOrder: (orderId: string, rate?: number) =>
    request<{ order: { id: string; productCostCents: number | null }; taxRefund: TaxRefundResult }>(
      rate !== undefined
        ? `/finance/tax-refund/${orderId}?rate=${rate}`
        : `/finance/tax-refund/${orderId}`,
    ),
  taxRefundSummary: (rate?: number) =>
    request<{ items: TaxRefundSummary[]; rate: number; total: number }>(
      rate !== undefined
        ? `/finance/tax-refund/summary?rate=${rate}`
        : '/finance/tax-refund/summary',
    ),

  // 现金流
  cashflow: (days: 30 | 60 | 90 = 30) =>
    request<CashflowForecast>(`/finance/cashflow?days=${days}`),
};

// === Holiday (v1.30 节日邮件, v1.32 用于 dashboard 倒计时) ===
export interface Holiday {
  key: string;
  name: { zh: string; en: string; ja: string };
  solarMonth: number;
  solarDay: number;
  /** 动态节日 (春节/中秋) 含 lunarMonth/lunarDay */
  dynamic?: { lunarMonth: number; lunarDay: number };
}

export interface TodayHolidayResponse {
  isHoliday: string | null;
  templatesAvailable: Array<'zh' | 'en' | 'ja'>;
}

export const holidayApi = {
  /** 列出 6 节日 */
  list: () => request<{ items: Holiday[] }>('/holidays'),
  /** 今天是哪个节日 (UTC) */
  today: () => request<TodayHolidayResponse>('/holidays/today'),
  /** 手动触发节日邮件 (dryRun=true 只预览) */
  send: (data: {
    holidayKey: string;
    customerIds?: string[];
    lang?: 'zh' | 'en' | 'ja';
    dryRun?: boolean;
  }) =>
    request<{
      holiday: string;
      dryRun: boolean;
      targetCount: number;
      sentCount: number;
      usedDryRun: boolean;
      sendErrors?: string[];
    }>('/holidays/send', { method: 'POST', body: data }),
};

// === v1.33: OCR Business Card ===
// Cloudflare Workers AI Vision 自动提取名片字段, 0 付费
export interface BusinessCardFields {
  company: string | null;
  contactName: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  wechat: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  industry: string | null;
  tags: string[];
  confidence: number | null;
}

export type OcrJobStatus = 'pending' | 'extracted' | 'imported' | 'failed';

export interface OcrJob {
  id: string;
  userId: string;
  companyId: string | null;
  imageR2Key: string;
  extractedJson: BusinessCardFields | null;
  source: 'upload' | 'camera';
  status: OcrJobStatus;
  errorMessage: string | null;
  customerId: string | null;
  createdAt: number;
  updatedAt: number;
}

export const ocrApi = {
  // multipart upload, 不走通用 request (自己 fetch)
  upload: (
    file: File,
  ): Promise<{
    jobId: string;
    imageUrl: string;
    imageR2Key: string;
    size: number;
    type: string;
    fields: BusinessCardFields;
    status: OcrJobStatus;
    errorMessage: string | null;
  }> => {
    const fd = new FormData();
    fd.append('card', file);
    return fetch('/api/ocr/business-card', {
      method: 'POST',
      body: fd,
      credentials: 'include',
    }).then(async (r) => {
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(
          (body as { error?: string; message?: string })?.message ??
            (body as { error?: string })?.error ??
            'OCR 识别失败',
        );
      }
      return body as {
        jobId: string;
        imageUrl: string;
        imageR2Key: string;
        size: number;
        type: string;
        fields: BusinessCardFields;
        status: OcrJobStatus;
        errorMessage: string | null;
      };
    });
  },
  import: (data: { fields: BusinessCardFields; jobId?: string }) =>
    request<{
      success: boolean;
      isNewCustomer: boolean;
      customerId: string;
      inquiryId: string;
    }>('/ocr/business-card/import', { method: 'POST', body: data }),
  listJobs: () => request<{ items: OcrJob[]; total: number }>('/ocr/jobs'),
};

// === v1.34: Intent Signal (拓客评分, 0 付费) ===
export type IntentGrade = 'A' | 'B' | 'C';

export interface BehavioralSignals {
  hiringOemManager: number;
  chinaSourcingMention: number;
  importToyMention: number;
  total: number;
}

export interface BusinessScaleSignals {
  employees50Plus: number;
  revenue5MPlus: number;
  decisionMakerLinkedin: number;
  total: number;
}

export interface ReachabilitySignals {
  decisionMakerLinkedin: number;
  websiteContactEmail: number;
  decisionMakerCLevel: number;
  total: number;
}

export interface IntentScore {
  totalScore: number;
  grade: IntentGrade;
  recommendation: string;
  behavioral: BehavioralSignals;
  businessScale: BusinessScaleSignals;
  reachability: ReachabilitySignals;
  scoredAt: number;
  source: 'dork-extract' | 'customer-fields' | 'manual';
}

export interface IntentDork {
  id: string;
  category: 'behavior' | 'reach' | 'scale';
  title: { zh: string; en: string; ja: string };
  query: string;
  signalTarget:
    | 'hiringOemManager'
    | 'chinaSourcingMention'
    | 'importToyMention'
    | 'employees50Plus'
    | 'revenue5MPlus'
    | 'decisionMakerLinkedin'
    | 'websiteContactEmail'
    | 'decisionMakerCLevel';
  reason: { zh: string; en: string; ja: string };
}

export const intentApi = {
  /** 5 个 intent dork 模板 (招 OEM / 中国供应链 / 进口玩具 / 联系页 / 公司规模) */
  listDorks: (query?: string) =>
    request<{ dorks: IntentDork[]; total: number }>(
      `/sourcing/intent-dorks${query ? `?query=${encodeURIComponent(query)}` : ''}`,
    ),
  /** 给一个客户 + Dork hits 算 Intent Score (A/B/C) */
  score: (data: {
    customerId?: string;
    companyName: string;
    country?: string;
    website?: string;
    dorkHits?: Array<{ title: string; url: string; snippet: string }>;
    decisionMakerName?: string;
    decisionMakerTitle?: string;
    decisionMakerHasLinkedin?: boolean;
  }) => request<{ score: IntentScore }>('/sourcing/intent-score', { method: 'POST', body: data }),
  /** 1 键入库 + 自动发 v1.30 初次接触邮件 (Resend) */
  import: (data: {
    name: string;
    country: string;
    website?: string;
    email?: string;
    contactPerson?: string;
    city?: string;
    product?: string;
    hsCode?: string;
    ourCompany?: string;
    intentGrade?: IntentGrade;
    intentScore?: IntentScore;
    sendEmail?: boolean;
    emailTemplate?: string;
    emailLang?: 'zh' | 'en' | 'ja';
  }) =>
    request<{
      customer: {
        id: string;
        name: string;
        grade: IntentGrade | 'D' | null;
        stage: string;
        email: string | undefined;
      };
      isNew: boolean;
      email: { sent: boolean; dryRun: boolean; subject?: string; error?: string };
      intentGrade: IntentGrade | null;
    }>('/sourcing/intent-import', { method: 'POST', body: data }),
};

// v1.34+: 自动拓客配置 (UI 配 PROSPECT_SEEDS / PROSPECT_QUERIES)
export const autoProspectApi = {
  get: () =>
    request<{
      seeds: string;
      queries: string;
      updatedAt: number | null;
      envFallback: { seeds: string; queries: string };
    }>('/settings/auto-prospect'),
  update: (data: { seeds?: string; queries?: string }) =>
    request<{ ok: boolean; updatedAt: number }>('/settings/auto-prospect', {
      method: 'PUT',
      body: data,
    }),
  runNow: () =>
    request<{
      ok: boolean;
      processed: number;
      imported: number;
      skipped: number;
      failed: number;
      autoFound: number;
      error?: string;
    }>('/settings/auto-prospect/run-now', { method: 'POST' }),
};

// === Pipeline ===
export interface PipelineFunnelStage {
  stage: Customer['stage'];
  label: string;
  count: number;
  toNextStage: number;
}

export interface PipelineStats {
  ok: true;
  timestamp: number;
  customer: {
    stages: Record<Customer['stage'], number>;
    total: number;
    enriched: number;
  };
  activity: { newInquiriesToday: number; newInquiriesWeek: number; newQuotesWeek: number };
  outreach: {
    sentThisWeek: number;
    repliedThisWeek: number;
    bouncedThisWeek: number;
    totalThisWeek: number;
    replyRate: number;
  };
  cycle: { avgDaysToOrder: number | null };
  backgroundCheck: Record<string, number>;
}

export const pipelineApi = {
  stats: () => request<PipelineStats>('/pipeline/stats'),
  funnel: () => request<{ ok: true; funnel: PipelineFunnelStage[] }>('/pipeline/funnel'),
};

// === v1.37.16: 背调运维 (批量重跑) ===
//
// 背景: v1.37.15 修好了 Workers AI 响应解析 bug (qwen3 推理模型
//   reasoning_content + JSON 截取被思考过程污染), 但生产存量记录
//   ai_model 全是 'fallback-rules', 不会自动更新, 需要手动重跑。
export interface BgOpsProbeResult {
  input: { name: string; email?: string; country: string };
  elapsedMs: number;
  aiModel: string;
  /** true = Workers AI 真的跑了; false = 走了 fallbackScan 兜底 */
  aiWorking: boolean;
  score: number;
  level: string;
  suggestedAction: string;
  aiSignals: Array<{ name: string; severity: string; evidence: string }>;
  freeSignals: Array<{ name: string; severity: string; evidence: string }>;
  mergedSignalCount: number;
}

export interface BgOpsRerunResult {
  ok: boolean;
  dryRun: boolean;
  companyId: string;
  targetCount: number;
  aiModelBreakdown: Record<string, number>;
  /** 关键判据: 真实 qwen3 模型名的记录数 > 0 = AI 修好了 */
  aiWorking: boolean;
  results: Array<{
    id: string;
    name: string;
    email?: string | null;
    aiModel: string;
    score: number;
    level: string;
    suggestedAction: string;
    signalCount: number;
    wrote: boolean;
    error?: string;
  }>;
}

export const bgOpsApi = {
  /** 单客户诊断 (不写库) */
  probe: (input: { name?: string; email?: string; country?: string } = {}) =>
    request<BgOpsProbeResult>('/bg-ops/probe', { method: 'POST', body: input }),
  /** 批量重跑 — dryRun 默认 true, 只报告不写库 */
  rerun: (opts: { dryRun?: boolean; customerId?: string } = {}) =>
    request<BgOpsRerunResult>('/bg-ops/rerun', {
      method: 'POST',
      body: { dryRun: opts.dryRun !== false, customerId: opts.customerId },
    }),
};

// === v1.38: 统一收件箱 ===
//
// 入站链路: Lark IMAP (Mac 本地同步器) → POST /api/inbound/email → D1 → GET /api/inbox
// 出站链路: Trade OS AI 出稿 → 复制 → 在 Lark 桌面端发送 (邮箱本体不动, 0 成本约束)

/** 收件箱筛选: 全部 / 未读 / 待回 / 逾期 */
export type InboxFilter = 'all' | 'unread' | 'pending' | 'overdue';

/** 线程内的一条邮件 (inquiry 即线程, message 是线程内的一封) */
export interface InboxMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  fromEmail: string;
  fromName?: string;
  toEmail: string;
  subject?: string;
  bodyOriginal?: string;
  /** AI 翻译成中文 (失败为 undefined) */
  bodyZh?: string;
  langDetected?: string;
  hasAttachments: boolean;
  attachmentNames: string[];
  source: string;
  sourceFolder?: string;
  isRead: boolean;
  createdAt: number;
}

/** 收件箱线程列表里的一项 (一个 inquiry = 一个线程) */
export interface InboxThread {
  inquiryId: string;
  customerId: string;
  customerName: string;
  customerCountry: string;
  subject: string;
  lastMessageAt: number;
  lastDirection: 'inbound' | 'outbound';
  unread: boolean;
  messageCount: number;
  status: string;
  /** 待回 = 最后一条来信晚于最后一条我方回复 */
  pending: boolean;
  /** 待回且距最后来信超过 3 天 */
  overdue: boolean;
  /** 距最后来信天数 */
  daysWaiting: number;
  /** 最后一条消息正文前 120 字 */
  preview: string;
}

export interface InboxListResponse {
  items: InboxThread[];
  counts: { all: number; unread: number; pending: number; overdue: number };
}

export const inboxApi = {
  /** 收件箱线程列表 (filter / search / limit) */
  list: (params: { filter?: InboxFilter; search?: string; limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.filter) search.set('filter', params.filter);
    if (params.search) search.set('search', params.search);
    if (params.limit) search.set('limit', String(params.limit));
    const q = search.toString();
    return request<InboxListResponse>(`/inbox${q ? `?${q}` : ''}`);
  },
};
